import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { red } from '@mui/material/colors';

import { trackEvent } from 'helpers/trackerHelpers';
import { ADMIN, SUPPORT } from 'business/constants';
import ModalForm from 'components/Form/ModalForm';
import ChartAlertModal from 'components/ChartAlert/ChartAlertModal';
import { useIsEssential, useIsExploreUser, useUserHasRole } from 'hooks/client';
import { DashboardChartT, LayoutT } from 'business/definitions';
import { useUpdateChartThenUpdateVizSettings } from 'routes/Explore/hooks/updateChartThenUpdateVizSettings';
import AddToDashboardModal from './AddToDashboardModal';
import { useCopyAnchor } from 'components/Widget/helpers/copyAnchor';
import WidgetContext from 'components/Widget/helpers/widgetContext';
import NestedMenu, {
    ChartActionType,
    NestedMenuOptionT,
} from 'ui/NestedMenu/NestedMenu';
import { useWidgetPositionContext } from 'lib/layout/context/WidgetPositionContext';
import {
    canDelete,
    canDuplicate,
    canInsert,
    canRename,
    canShowEdit,
    displayFirstSeparator,
    displayLastSeparator,
    shouldNotDisplayChartEditMenu,
} from './helpers/menuContent';
import {
    getDashboardParamForExploreRoute,
    getWidgetPositionParamForExploreRoute,
} from 'routes/Explore/helpers';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useDeleteChart from 'hooks/useDeleteChart';
import { useQueryClient } from '@tanstack/react-query';
import useAddWidget from 'hooks/useAddWidget';
import { useDispatch } from 'store/hooks';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import { getContainerFromParams } from 'lib/layout/helper/containerHelper';
import { CHART_ACTION_TYPE } from 'ui/NestedMenu/constants';

type CommonProps = {
    chart?: DashboardChartT;
    chartKey: number;
    chartTitle: string | null | undefined;
    canEdit?: boolean;
    editMode?: boolean;
    toggleEditMode?: () => void;
    isHeader?: boolean;
};

type PropsWithoutDisplayMenuOnly = CommonProps & {
    displayMenuOnly?: false;
};

type PropsWithDisplayMenuOnly = CommonProps & {
    displayMenuOnly: true;
    anchorPosition: { top: number; left: number };
    onClose: () => void;
};

type Props = PropsWithDisplayMenuOnly | PropsWithoutDisplayMenuOnly;

const isDisplayMenuProps = (props: Props): props is PropsWithDisplayMenuOnly =>
    !!props.displayMenuOnly && 'anchorPosition' in props && 'onClose' in props;

const ChartEditMenu = (props: Props): JSX.Element => {
    const {
        chart = undefined,
        chartKey,
        chartTitle = '',
        canEdit = false,
        editMode = false,
        toggleEditMode = undefined,
        isHeader = false,
        displayMenuOnly = false,
    } = props;

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { slug } = useParams<{ slug: string }>();
    const isExploreUser = useIsExploreUser();
    const { updateChartThenUpdateVizSettings } =
        useUpdateChartThenUpdateVizSettings();
    const userIsSupport = useUserHasRole(SUPPORT);
    const userIsAdmin = useUserHasRole(ADMIN);
    const hasEssential = useIsEssential();
    const displayAlert = !isHeader;
    const widgetPosition = useWidgetPositionContext();
    const { mutate: deleteChart } = useDeleteChart();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();

    const [shownModal, setShownModal] = React.useState<ChartActionType | null>(
        null,
    );
    const { budgetProposalId } = useParams<{ budgetProposalId?: string }>();

    const addWidgetMutation = useAddWidget(
        slug,
        getPublicToken(),
        (err) =>
            dispatch.vision.notifyUser({
                message: err.message,
                snackType: 'error',
            }),
        getContainerFromParams(budgetProposalId), //change with useParams
    );

    const closeMenu = () => {
        setShownModal(null);
    };

    const widget = React.useContext(WidgetContext);
    const copyAnchor = useCopyAnchor();

    const onRenameChartSuccess = (title: string) =>
        queryClient.setQueryData(['getLayout', slug, ''], (layout: LayoutT) => {
            return {
                ...layout,
                widgets: layout.widgets.map((existingWidget) =>
                    existingWidget.chart?.key === chartKey
                        ? {
                              ...existingWidget,
                              chart: { ...existingWidget.chart, title },
                          }
                        : existingWidget,
                ),
            };
        });

    const renameChart = ({ title }: { title: string }) => {
        if (title !== chartTitle)
            updateChartThenUpdateVizSettings({
                chartData: { title },
                chartKey,
                redirect: false,
                dashboardSlug: slug,
                onSuccess: () => onRenameChartSuccess(title),
            });
        closeMenu();
    };
    const handleDeleteChart = () => {
        if (widget && slug) {
            deleteChart({ widgetKey: widget.key, slug });
        }
        closeMenu();
    };

    const displayDuplicate = canDuplicate(hasEssential, userIsSupport);
    const displayEdit = canShowEdit(
        canEdit,
        isExploreUser,
        hasEssential,
        userIsAdmin,
    );
    const displayRename = canRename(canEdit);
    const displayResize =
        canEdit && editMode !== undefined && toggleEditMode !== undefined;
    const displayDelete = canDelete(
        canEdit,
        hasEssential,
        userIsAdmin,
        userIsSupport,
    );
    const displayInsert = canInsert(canEdit, isExploreUser);

    if (
        shouldNotDisplayChartEditMenu(
            displayAlert,
            displayDelete,
            displayDuplicate,
            displayEdit,
            displayInsert,
            displayRename,
            displayResize,
        )
    ) {
        return <></>;
    }
    const getMenuContent = () => {
        const menuContent: NestedMenuOptionT[] = [];
        if (displayDuplicate)
            menuContent.push({
                action: CHART_ACTION_TYPE.Duplicate,
                onClick: setShownModal,
                menuLevel: 0,
                separator: displayFirstSeparator(
                    displayAlert,
                    displayDelete,
                    displayDuplicate,
                    displayEdit,
                    displayRename,
                    displayResize,
                ),
            });

        if (widget)
            menuContent.push({
                action: CHART_ACTION_TYPE.CopyAnchor,
                menuLevel: 0,
                onClick: () => {
                    copyAnchor(widget.key);
                    closeMenu();
                },
            });

        if (displayEdit)
            menuContent.push({
                action: CHART_ACTION_TYPE.Edit,
                onClick: () => {
                    navigate(
                        `/explore/${chartKey}${getDashboardParamForExploreRoute(slug)}`,
                    );
                    trackEvent(
                        'decision/dashboard/indicators-options/open-in-explore-click',
                    );
                },
                menuLevel: 0,
            });

        if (displayAlert)
            menuContent.push({
                action: CHART_ACTION_TYPE.Alert,
                onClick: setShownModal,
                menuLevel: 0,
            });

        if (displayRename)
            menuContent.push({
                action: CHART_ACTION_TYPE.Rename,
                onClick: setShownModal,
                menuLevel: 0,
            });

        if (displayResize)
            menuContent.push({
                action: CHART_ACTION_TYPE.Resize,
                onClick: () => (!editMode ? toggleEditMode() : undefined),
                menuLevel: 0,
            });

        if (displayDelete)
            menuContent.push({
                action: CHART_ACTION_TYPE.Delete,
                onClick: setShownModal,
                menuLevel: 0,
                separator: displayLastSeparator(
                    displayAlert,
                    displayDelete,
                    displayInsert,
                    displayEdit,
                    displayRename,
                    displayResize,
                ),
            });

        if (widget && displayInsert)
            menuContent.push({
                action: CHART_ACTION_TYPE.Insert,
                menuLevel: 0,
                onClick: () => {},
                nestedOptions: [
                    {
                        action: CHART_ACTION_TYPE.InsertIndicator,
                        onClick: () =>
                            navigate(
                                `/explore${getDashboardParamForExploreRoute(slug)}&${getWidgetPositionParamForExploreRoute(widgetPosition)}`,
                            ),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.InsertTitle,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'title',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.ViewHeadline,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'paragraph',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.Separator,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'separator',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.AddLink,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'navlink',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.InsertPageBreak,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'pagebreak',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                    {
                        action: CHART_ACTION_TYPE.FormatBulletedList,
                        onClick: () =>
                            addWidgetMutation.mutate({
                                type: 'tableofcontent',
                                layoutId: widget.layoutId,
                                position: widgetPosition,
                            }),
                        menuLevel: 1,
                    },
                ],
            });
        return menuContent;
    };

    return (
        <>
            {!displayMenuOnly && (
                <NestedMenu
                    button={{
                        isIconButton: true,
                        iconButton: {
                            iconElement: <MoreVertIcon fontSize="small" />,
                            className: '',
                            color: 'primary',
                        },
                    }}
                    menuLevels={2}
                    options={getMenuContent()}
                    onOptionClick={(option: NestedMenuOptionT) =>
                        option.onClick(option.action)
                    }
                />
            )}
            {displayMenuOnly && isDisplayMenuProps(props) && (
                <NestedMenu
                    menuLevels={2}
                    options={getMenuContent()}
                    onOptionClick={(option: NestedMenuOptionT) =>
                        option.onClick(option.action)
                    }
                    anchorPosition={props.anchorPosition}
                />
            )}
            {shownModal === CHART_ACTION_TYPE.Rename && (
                <ModalForm
                    show
                    fullWidth
                    closeModal={closeMenu}
                    submit={renameChart}
                    submitText={t('common.save')}
                    title={t('chart.rename')}
                    fields={[
                        {
                            key: 'title',
                            name: 'title',
                            label: t('common.title'),
                            type: 'text',
                            value: chartTitle,
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-update-dashboard-button"
                />
            )}
            {shownModal === CHART_ACTION_TYPE.Duplicate && (
                <AddToDashboardModal
                    show
                    isInDashboard
                    closeModal={closeMenu}
                    chartKey={chartKey}
                    widgetPosition={widgetPosition}
                />
            )}
            {shownModal === CHART_ACTION_TYPE.Delete && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={handleDeleteChart}
                    submitText={t('common.delete')}
                    title={t('common.deleteConfirm')}
                    confirmButtonColor={red[900]}
                    submitClass="e2e-delete-chart-button"
                />
            )}
            {shownModal === CHART_ACTION_TYPE.Alert && chart && (
                <ChartAlertModal
                    chart={chart}
                    chartTitle={chartTitle}
                    show
                    closeModal={closeMenu}
                />
            )}
        </>
    );
};

export default ChartEditMenu;
